import { Link } from 'react-router-dom';
import type { QuoteRecord, Ticket } from '../../types';
import { normalizePassengers } from '../../lib/passengers';
import {
  isQuoteFollowUpTicket,
  quoteEmailWasSent,
  ticketReceiptEmailWasSent,
} from '../../lib/quoteTicketLink';

function formatLegDate(travelDate?: string): string {
  if (!travelDate) return '—';
  try {
    const d = new Date(travelDate + 'T12:00:00');
    if (Number.isNaN(d.getTime())) return travelDate;
    return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  } catch {
    return travelDate;
  }
}

function statusBadgeClass(status: string): string {
  if (status === 'open') return 'ticket-desk__badge--open';
  if (status === 'in_progress') return 'ticket-desk__badge--progress';
  if (status === 'waiting_customer') return 'ticket-desk__badge--waiting';
  if (status === 'resolved') return 'ticket-desk__badge--resolved';
  return 'ticket-desk__badge--closed';
}

function priorityBadgeClass(priority: string): string {
  return `ticket-desk__badge--priority-${priority}`;
}

type Props = {
  ticket: Ticket;
  quote: QuoteRecord | undefined;
  agentId: string;
  sendingTicketId: string | null;
  sendingOafId: string | null;
  onSendTicketReceipt: () => void;
  onSendOafEmail: () => void;
  onCopy: (text: string, label: string) => void;
  onConfirmPayment: () => void;
  onConfirmAndSendTicketReceipt: () => void;
  onResendAllEmails: () => void;
  resendAllInProgress: boolean;
};

export default function TicketQuotePanel({
  ticket,
  quote,
  agentId,
  sendingTicketId,
  sendingOafId,
  onSendTicketReceipt,
  onSendOafEmail,
  onCopy,
  onConfirmPayment,
  onConfirmAndSendTicketReceipt,
  onResendAllEmails,
  resendAllInProgress,
}: Props) {
  const quoteId = quote?.id;
  const quoteLinked = isQuoteFollowUpTicket(ticket);

  if (!quoteLinked) {
    return (
      <section className="ticket-desk__section">
        <h4 className="ticket-desk__section-title">Trip</h4>
        <p className="ticket-desk__description" style={{ color: 'var(--text-muted)' }}>
          Manual ticket — not linked to a quote. Use Email client from the header actions if needed.
        </p>
      </section>
    );
  }

  if (!quote) {
    return (
      <div className="ticket-desk__alert ticket-desk__alert--warn">
        Linked quote <code>{quoteId ?? '—'}</code> could not be loaded. Click Refresh or check the API server.
      </div>
    );
  }

  const paid = quote.paymentStatus === 'paid';
  const travelers = normalizePassengers(quote);
  const paxCount = Math.max(1, travelers.length);
  const total = quote.pricePerPerson * paxCount;
  const f = quote.flight;
  const quoteSent = quoteEmailWasSent(quote);
  const oafSent = Boolean(quote.emailsSent?.oaf);
  const ticketSent = ticketReceiptEmailWasSent(quote);
  const canSendTicket = paid && !ticketSent;

  return (
    <>
      <section className="ticket-desk__section">
        <h4 className="ticket-desk__section-title">Flight &amp; fare</h4>
        <div className="ticket-desk__quote-grid">
          <dl className="ticket-desk__info-card">
            <dt>Airline / flight</dt>
            <dd>
              {f.airline} {f.flightNumber}
            </dd>
          </dl>
          <dl className="ticket-desk__info-card">
            <dt>Route</dt>
            <dd>
              {f.departure} → {f.arrival}
              {f.returnLeg ? ` · return ${f.returnLeg.arrival}` : ''}
            </dd>
          </dl>
          <dl className="ticket-desk__info-card">
            <dt>Travel date</dt>
            <dd>{formatLegDate(f.travelDate)}</dd>
          </dl>
          <dl className="ticket-desk__info-card">
            <dt>Quoted total</dt>
            <dd>
              ${total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USD
              <span style={{ display: 'block', fontWeight: 500, fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                ${quote.pricePerPerson.toLocaleString('en-US', { minimumFractionDigits: 2 })} × {paxCount} traveler
                {paxCount !== 1 ? 's' : ''}
              </span>
            </dd>
          </dl>
          <dl className="ticket-desk__info-card">
            <dt>Confirmation</dt>
            <dd>{quote.confirmationNumber ?? '—'}</dd>
          </dl>
          <dl className="ticket-desk__info-card">
            <dt>Payment</dt>
            <dd>
              <span className={`ticket-desk__badge ${paid ? 'ticket-desk__badge--paid' : 'ticket-desk__badge--unpaid'}`}>
                {paid ? 'Paid' : 'Awaiting payment'}
              </span>
            </dd>
          </dl>
        </div>
      </section>

      <section className="ticket-desk__section">
        <h4 className="ticket-desk__section-title">Passengers ({travelers.length})</h4>
        {travelers.length > 0 && travelers[0]!.name.trim() ? (
          <table className="ticket-desk__passengers-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Name (as on passport)</th>
                <th>Age</th>
                <th>Date of birth</th>
              </tr>
            </thead>
            <tbody>
              {travelers.map((p, i) => (
                <tr key={`${p.name}-${i}`}>
                  <td>{i + 1}</td>
                  <td style={{ fontWeight: 700 }}>{p.name.toUpperCase()}</td>
                  <td>{p.age ?? '—'}</td>
                  <td>{p.dateOfBirth ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="ticket-desk__passengers-empty">
            No passenger details saved yet. The client should complete the{' '}
            <strong>booking</strong> link and enter traveler names before payment.
            {quote.bookingUrl ? (
              <>
                {' '}
                <button type="button" className="ticket-desk__link-btn" onClick={() => onCopy(quote.bookingUrl, 'Booking link')}>
                  Copy booking link
                </button>
              </>
            ) : null}
          </div>
        )}
        {quote.bookingUpdatedAt ? (
          <p style={{ margin: 0, fontSize: '0.8rem', color: 'var(--text-muted)' }}>
            Last saved from booking: {new Date(quote.bookingUpdatedAt).toLocaleString()}
          </p>
        ) : null}
      </section>

      <section className="ticket-desk__section">
        <h4 className="ticket-desk__section-title">Client contact</h4>
        <div className="ticket-desk__quote-grid">
          <dl className="ticket-desk__info-card">
            <dt>Name</dt>
            <dd>{quote.clientSnapshot.name}</dd>
          </dl>
          <dl className="ticket-desk__info-card">
            <dt>Email</dt>
            <dd>{quote.clientSnapshot.email || '—'}</dd>
          </dl>
          <dl className="ticket-desk__info-card">
            <dt>Phone</dt>
            <dd>{quote.clientSnapshot.phone ?? '—'}</dd>
          </dl>
        </div>
        {quote.clientId && agentId ? (
          <Link
            to={`/admin/agent/${agentId}/clients/${quote.clientId}`}
            style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--accent)' }}
          >
            Open client profile →
          </Link>
        ) : null}
      </section>

      <section className="ticket-desk__section">
        <h4 className="ticket-desk__section-title">Emails sent</h4>
        <div className="ticket-desk__email-grid">
          <div className={`ticket-desk__email-chip ${quoteSent ? 'is-done' : 'is-pending'}`}>
            <strong>Quote email</strong>
            {quoteSent && quote.emailsSent?.quote
              ? new Date(quote.emailsSent.quote).toLocaleDateString()
              : 'Not sent'}
          </div>
          <div className={`ticket-desk__email-chip ${oafSent ? 'is-done' : 'is-pending'}`}>
            <strong>OAF payment</strong>
            {oafSent && quote.emailsSent?.oaf ? new Date(quote.emailsSent.oaf).toLocaleDateString() : 'Not sent'}
          </div>
          <div className={`ticket-desk__email-chip ${ticketSent ? 'is-done' : 'is-pending'}`}>
            <strong>E-ticket receipt</strong>
            {ticketSent && quote.emailsSent?.ticket
              ? new Date(quote.emailsSent.ticket).toLocaleDateString()
              : 'Not sent'}
          </div>
        </div>
      </section>

      <section className="ticket-desk__section">
        <h4 className="ticket-desk__section-title">Links</h4>
        <div className="ticket-desk__links">
          <button type="button" className="ticket-desk__link-btn" onClick={() => onCopy(quote.bookingUrl, 'Booking link')}>
            Copy booking
          </button>
          <button type="button" className="ticket-desk__link-btn" onClick={() => onCopy(quote.paymentUrl, 'Payment link')}>
            Copy payment
          </button>
        </div>
      </section>

      <section className="ticket-desk__section">
        <h4 className="ticket-desk__section-title">Actions</h4>
        {!paid ? (
          <>
            <p style={{ margin: '0 0 0.65rem', fontSize: '0.88rem', color: 'var(--text-muted)', lineHeight: 1.45 }}>
              Client must pay on the payment link before you can send the e-ticket receipt.
            </p>
            <button
              type="button"
              className="ticket-desk__btn"
              disabled={resendAllInProgress || sendingTicketId === ticket.id || sendingOafId === ticket.id}
              onClick={onSendOafEmail}
            >
              {sendingOafId === ticket.id ? 'Sending OAF…' : 'Send OAF'}
            </button>
            <button
              type="button"
              className="ticket-desk__btn"
              disabled={resendAllInProgress || sendingTicketId === ticket.id || sendingOafId === ticket.id}
              onClick={onConfirmPayment}
            >
              Confirm payment
            </button>
            <button
              type="button"
              className="ticket-desk__btn ticket-desk__btn--primary"
              disabled={resendAllInProgress || sendingTicketId === ticket.id || sendingOafId === ticket.id}
              onClick={onConfirmAndSendTicketReceipt}
            >
              Confirm & send e-ticket
            </button>
          </>
        ) : (
          <>
            {ticketSent ? (
              <p style={{ margin: '0 0 0.65rem', fontSize: '0.88rem', color: '#047857' }}>E-ticket already delivered (you can resend).</p>
            ) : (
              <p style={{ margin: '0 0 0.65rem', fontSize: '0.88rem', color: '#047857' }}>Payment is marked as paid.</p>
            )}
            <button
              type="button"
              className="ticket-desk__btn"
              disabled={resendAllInProgress || sendingOafId === ticket.id}
              onClick={onSendOafEmail}
              style={{ marginBottom: '0.75rem' }}
            >
              {sendingOafId === ticket.id ? 'Sending OAF…' : 'Send OAF'}
            </button>
            <button
              type="button"
              className="ticket-desk__btn"
              disabled={resendAllInProgress || sendingOafId === ticket.id}
              onClick={onResendAllEmails}
              style={{ marginBottom: '0.75rem' }}
            >
              {resendAllInProgress ? 'Resending emails…' : 'Resend all emails (quote → OAF → e-ticket)'}
            </button>
            <button
              type="button"
              className="ticket-desk__btn ticket-desk__btn--primary"
              disabled={!canSendTicket || sendingTicketId === ticket.id}
              onClick={onSendTicketReceipt}
            >
              {sendingTicketId === ticket.id ? 'Sending e-ticket…' : 'Send e-ticket email'}
            </button>
          </>
        )}
      </section>
    </>
  );
}

export { statusBadgeClass, priorityBadgeClass };
